import React from 'react';
import { Box, Container, Link, Stack, Typography } from '@mui/material';
import type { LandingCopy, LandingPalette } from './landingCopy';

type LandingFooterProps = {
  copy: LandingCopy['footer'];
  palette: LandingPalette;
  themeMode: 'dark' | 'light';
};

const LandingFooter: React.FC<LandingFooterProps> = ({ copy, palette, themeMode }) => {
  const footerText = themeMode === 'dark' ? '#fff' : '#F8FAFC';
  const muted = 'rgba(255,255,255,0.55)';

  return (
    <Box
      component="footer"
      sx={{
        py: { xs: 1.5, md: 1.75 },
        borderTop: `1px solid ${palette.border}`,
        bgcolor: palette.footerBg,
        color: footerText,
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1.3fr 1fr' },
            gap: { xs: 1.25, md: 2 },
            mb: 1.25,
            alignItems: 'start',
          }}
        >
          <Box>
            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1 }}>
              <Box
                component="img"
                src="/image/Logo.jpeg"
                alt="ELOG"
                sx={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover' }}
              />
              <Box>
                <Typography sx={{ fontFamily: '"Outfit", "Cairo", sans-serif', fontWeight: 800, lineHeight: 1.2 }}>
                  ELOG
                </Typography>
                <Typography variant="caption" sx={{ color: muted, letterSpacing: '0.06em' }}>
                  Aviation Electronic Logbook
                </Typography>
              </Box>
            </Stack>
            <Typography variant="body2" sx={{ color: muted, maxWidth: 380, lineHeight: 1.55, fontSize: '0.85rem' }}>
              {copy.tagline}
            </Typography>
          </Box>

          <Box>
            <Typography
              sx={{
                fontFamily: '"Outfit", "Cairo", sans-serif',
                fontWeight: 700,
                mb: 1,
                fontSize: '0.9rem',
              }}
            >
              {copy.contact}
            </Typography>
            <Stack spacing={0.5}>
              <Link
                href="mailto:info@skyofsolutions.com"
                underline="hover"
                sx={{ color: '#7DD3FC', fontSize: '0.85rem' }}
              >
                info@skyofsolutions.com
              </Link>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.5, sm: 2 }}>
                <Link
                  href="tel:+962799970098"
                  underline="hover"
                  sx={{ color: muted, fontSize: '0.85rem' }}
                >
                  +962799970098
                </Link>
                <Link
                  href="tel:+962796933583"
                  underline="hover"
                  sx={{ color: muted, fontSize: '0.85rem' }}
                >
                  +962796933583
                </Link>
              </Stack>
              <Box sx={{ pt: 0.75, display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="caption" sx={{ color: muted }}>
                  {copy.companion}:
                </Typography>
                <Link
                  href="https://avisky.skyofsolutions.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                  sx={{ color: '#7DD3FC', fontWeight: 600, fontSize: '0.85rem' }}
                >
                  Avi Sky™
                </Link>
              </Box>
            </Stack>
          </Box>
        </Box>

        <Box
          sx={{
            pt: 1,
            borderTop: '1px solid rgba(255,255,255,0.1)',
          }}
        >
          <Link
            href="https://www.skyofsolutions.com/en"
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            variant="caption"
            sx={{ color: 'rgba(255,255,255,0.4)' }}
          >
            © {new Date().getFullYear()} Sky Solutions. {copy.rights}
          </Link>
        </Box>
      </Container>
    </Box>
  );
};

export default LandingFooter;
